<script setup lang="ts">
import { computed } from 'vue'
import type { ITariffCardProps } from '@/types/ITariffCardProps.ts'
import { useI18n } from 'vue-i18n'

const props = defineProps<ITariffCardProps>()
defineEmits(['select'])

const { t } = useI18n()

const cardClass = computed(() => {
  return props.tariff.tagType === 'popular' ? 'tariff-card-default' : ''
})
const tagClass = computed(() => {
  if (props.tariff.tagType === 'popular') return 'popular'
  if (props.tariff.tagType === 'profitable') return 'profitable'
  return ''
})
</script>

<template>
  <label class="cursor-pointer">
    <span
      :class="[
        'tariff-card d-flex align-center ga-3 pa-4 bg-surface rounded-xl',
        cardClass,
        { 'tariff-card--checked': checked },
      ]"
    >
      <span class="tariff-card__input-wrapper d-flex align-center justify-center">
        <input
          :id="`tariff-${tariff.value}`"
          class="tariff-card__input"
          name="tariff"
          type="radio"
          :value="tariff.value"
          :checked="checked"
          @change="$emit('select', tariff.value)"
        />
      </span>

      <span class="d-flex justify-space-between w-100">
        <span class="d-flex flex-column">
          <span class="tariff-card__duration">
            {{ tariff.duration }} <span class="currency">₽</span>
          </span>
          <span v-if="tariff.tag" :class="['tariff-card__tag', tagClass]">{{ tariff.tag }}</span>
        </span>

        <span v-if="tariff.economy" class="d-flex align-start">
          <span class="tariff-card__economy">
            {{ tariff.economy }} <span class="tariff-card__economy-unit">{{ t('common.perMonth') }}</span>
          </span>
        </span>
      </span>
    </span>
  </label>
</template>

<style scoped>
.tariff-card {
  height: 65px;
  border: 1px solid transparent;
  transition: border-color 0.2s;
}

.tariff-card__input-wrapper {
  width: 16px;
  height: 100%;
}

.tariff-card--checked,
.tariff-card:focus-within {
  border: 1px solid rgb(var(--v-theme-primary)) !important;
}

.tariff-card:active {
  border-color: rgb(var(--v-theme-primary));
}

.tariff-card__input {
  width: 16px;
  height: 16px;
  margin: 0;
  appearance: none;
  border: 2px solid rgba(var(--v-theme-light), 0.3);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.tariff-card__input:checked::before {
  content: '';
  width: 8px;
  height: 8px;
  background-color: rgb(var(--v-theme-primary));
  border-radius: 50%;
}

.tariff-card__duration {
  font-size: 16px;
  font-weight: 500;
  line-height: 110%;
  color: rgb(var(--v-theme-on-surface));
}

.tariff-card__tag {
  font-size: 14px;
  font-weight: 400;
  line-height: 110%;
}

.tariff-card__tag.popular {
  color: rgb(var(--v-theme-success));
}

.tariff-card__tag.profitable {
  color: rgb(var(--v-theme-text-disabled));
}

.tariff-card__economy {
  width: 62px;
  font-size: 15px;
  font-weight: 400;
  line-height: 110%;
  color: rgb(var(--v-theme-on-surface));
}

.tariff-card__economy-unit {
  font-size: 11px;
  font-weight: 400;
  margin-left: 2px;
}

.currency {
  font-size: 0.85em;
  vertical-align: super;
}
</style>
